import api from './api';

export interface AttachmentUploadResponse {
  success: boolean;
  file_id: string;
  filename: string;
  size: number;
  type: string;
  transcription?: string;
  preview_url?: string;
}

export interface TranscriptionResponse {
  success: boolean;
  transcription: string;
  filename: string;
}

export interface TTSResponse {
  success: boolean;
  audio_url?: string;
  audio_id?: string;
  audio_data?: string;
  content_type?: string;
}

/**
 * Upload de arquivo para anexar ao chat
 */
export const uploadFile = async (file: File): Promise<AttachmentUploadResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    // Não especificar Content-Type - o Axios define automaticamente com o boundary correto
    const response = await api.post('/chat/upload/upload', formData);

    return response.data;
  } catch (error) {
    console.error('Error uploading file:', error);
    throw error;
  }
};

/**
 * Transcrever áudio usando Whisper API
 */
export const transcribeAudio = async (file: File): Promise<TranscriptionResponse> => {
  try {
    const formData = new FormData();
    formData.append('file', file);

    // Não especificar Content-Type - o Axios define automaticamente com o boundary correto
    const response = await api.post('/chat/upload/transcribe', formData);

    return response.data;
  } catch (error) {
    console.error('Error transcribing audio:', error);
    throw error;
  }
};

/**
 * Converter texto em áudio usando TTS
 */
export const textToSpeech = async (text: string): Promise<TTSResponse> => {
  try {
    const response = await api.post('/chat/upload/speak', { text });
    return response.data;
  } catch (error) {
    console.error('Error converting text to speech:', error);
    throw error;
  }
};

/**
 * Download de arquivo anexado
 */
export const downloadAttachment = async (fileId: string, filename: string): Promise<void> => {
  try {
    const response = await api.get(`/chat/attachments/${fileId}/download`, {
      responseType: 'blob',
    });

    // Criar URL para download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Deletar anexo
 */
export const deleteAttachment = async (fileId: string): Promise<void> => {
  try {
    await api.delete(`/chat/attachments/${fileId}`);
  } catch (error) {
    console.error('Error deleting attachment:', error);
    throw error;
  }
};

/**
 * Obter URL de preview de imagem
 */
export const getPreviewUrl = (fileId: string): string => {
  return `${api.defaults.baseURL}/chat/attachments/${fileId}/preview`;
};

/**
 * Obter URL de download
 */
export const getDownloadUrl = (fileId: string): string => {
  return `${api.defaults.baseURL}/chat/attachments/${fileId}/download`;
};

/**
 * Validar tipo de arquivo
 */
export const validateFileType = (file: File): { isValid: boolean; type?: string; error?: string } => {
  const extension = file.name.split('.').pop()?.toLowerCase();
  
  const typeMap: { [key: string]: string } = {
    // Documentos
    'pdf': 'document',
    'doc': 'document', 
    'docx': 'document',
    'txt': 'document',
    'rtf': 'document',
    
    // Imagens
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'bmp': 'image',
    'webp': 'image',
    
    // Áudios
    'mp3': 'audio',
    'wav': 'audio',
    'm4a': 'audio',
    'ogg': 'audio',
    'flac': 'audio'
  };

  if (!extension || !typeMap[extension]) {
    return {
      isValid: false,
      error: `Tipo de arquivo não suportado: .${extension}`
    };
  }

  const type = typeMap[extension];
  const maxSizes: { [key: string]: number } = {
    document: 50 * 1024 * 1024, // 50MB
    image: 10 * 1024 * 1024,    // 10MB
    audio: 25 * 1024 * 1024     // 25MB
  };

  if (file.size > maxSizes[type]) {
    const maxMB = maxSizes[type] / (1024 * 1024);
    return {
      isValid: false,
      error: `Arquivo muito grande. Máximo para ${type}: ${maxMB}MB`
    };
  }

  return {
    isValid: true,
    type
  };
};

/**
 * Formatar tamanho do arquivo
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};
